<template>
  <div
    :class="[$style.gallery, isModal && $style.galleryInModal]"
    data-product-gallery-v="3.1.0"
  >
    <div :class="$style.activeImageWrapper">
      <div :class="$style.controlWrapper">
        <button
          v-if="computedHasPrevImage"
          :class="$style.control"
          :aria-label="localizedStrings['previous'] || 'Previous'"
          @click="showPrevImage"
        >
          <IconChevronLeft :class="$style.chevronLeft" />
        </button>
      </div>
      <div v-if="activeImage" :class="$style.imageWrapper" @click="openModal">
        <BasePicture
          :img-class="$style.image"
          :src="computedActiveImageUrl"
          alt=""
        />
      </div>
      <div :class="$style.controlWrapper">
        <button
          v-if="computedHasNextImage"
          :class="$style.control"
          :aria-label="localizedStrings['next'] || 'Next'"
          @click="showNextImage"
        >
          <IconChevronRight :class="$style.chevronRight" />
        </button>
      </div>
    </div>
    <template v-if="images && images.length > 1">
      <div
        ref="productGalleryThumbnailsSwiper"
        :class="['swiper', $style.thumbnails]"
      >
        <div class="swiper-wrapper">
          <div
            v-for="(image, index) in images"
            :key="`image-${image?.sys?.id}`"
            :class="['swiper-slide', $style.thumbnailWrapper]"
            @click="changeActiveImage(index)"
          >
            <BasePicture
              v-if="image?.fields?.file?.url"
              :img-class="[
                $style.thumbnail,
                image?.fields?.file?.url === activeImage ? $style.active : '',
              ]"
              :src="`${image?.fields?.file?.url}?w=128&h=84&fit=pad`"
              alt=""
            />
          </div>
        </div>
      </div>
      <div :class="$style.pagination">
        <div
          v-for="(image, index) in images"
          :key="`pagination-${image?.sys?.id}`"
          :class="[$style.dot, activeImageIndex === index && $style.active]"
          @click="changeActiveImage(index)"
        />
      </div>
    </template>
    <div
      v-if="hasMarketingMaterials"
      :class="$style.directToMarketingMaterials"
    >
      <NuxtLinkLocale to="#marketing-materials" @click="emit('request-close')">
        {{ localizedStrings['direct-to-marketing-materials'] || '' }}
      </NuxtLinkLocale>
    </div>
  </div>
</template>

<script setup lang="ts">
import Swiper from 'swiper'

import type { Asset } from 'contentful'

import 'swiper/css'

const { localizedStrings } = useLocalizedStrings()

const emit = defineEmits(['open-modal', 'request-close'])

const props = defineProps<{
  images?: (Asset | undefined)[]
  isModal?: boolean
  hasMarketingMaterials?: boolean
}>()

// Data
const activeImage = ref(props?.images?.[0]?.fields?.file?.url)
const activeImageIndex = ref(0)
const thumbnailsElRef = useTemplateRef<HTMLElement>(
  'productGalleryThumbnailsSwiper',
)
let thumbnailsSwiper: Swiper | null = null
let thumbnailsResizeObserver: ResizeObserver | null = null

// Computed
const computedHasPrevImage = computed(() => {
  return props.images?.[activeImageIndex.value - 1]?.fields?.file?.url || false
})

const computedHasNextImage = computed(() => {
  return props.images?.[activeImageIndex.value + 1]?.fields?.file?.url || false
})

const computedActiveImageUrl = computed(() => {
  const width = props.isModal ? 600 : 300
  const height = props.isModal ? 600 : 300
  return `${activeImage.value}?w=${width}&h=${height}&fit=pad`
})

// Methods
const changeActiveImage = (index = -1) => {
  if (index > -1) {
    activeImageIndex.value = index
  }
  activeImage.value =
    props.images?.[activeImageIndex.value]?.fields?.file?.url || ''
}

const showPrevImage = () => {
  activeImageIndex.value = activeImageIndex.value - 1
  changeActiveImage()
}

const showNextImage = () => {
  activeImageIndex.value = activeImageIndex.value + 1
  changeActiveImage()
}

const openModal = () => {
  if (props.isModal) return
  emit('open-modal')
}

const initThumbnailsSwiper = () => {
  if (thumbnailsSwiper || !thumbnailsElRef.value) return
  thumbnailsSwiper = new Swiper(thumbnailsElRef.value, {
    slidesPerView: 2,
    spaceBetween: 10,
    centerInsufficientSlides: true,
    watchSlidesProgress: true,
    breakpoints: {
      576: { slidesPerView: 3 },
    },
  })
  thumbnailsSwiper.slideTo(activeImageIndex.value, 0)

  // Recompute once the container width settles (zero at mount when hidden)
  if (thumbnailsElRef.value) {
    thumbnailsResizeObserver = new ResizeObserver(() => {
      thumbnailsSwiper?.update()
    })
    thumbnailsResizeObserver.observe(thumbnailsElRef.value)
  }
}

// Watchers
watch(activeImageIndex, (index) => {
  thumbnailsSwiper?.slideTo(index)
})

watch(thumbnailsElRef, (el) => {
  if (el) initThumbnailsSwiper()
})

onMounted(() => initThumbnailsSwiper())

onBeforeUnmount(() => {
  thumbnailsResizeObserver?.disconnect()
  thumbnailsResizeObserver = null
  thumbnailsSwiper?.destroy(true, true)
  thumbnailsSwiper = null
})
</script>

<style lang="scss" module>
.gallery {
  background-color: var(--white);
  box-shadow: 0 4px 13px 0 var(--black-alpha10);
  padding: 20px 8px;
  min-height: 320px;
}

.galleryInModal {
  @include media('sm') {
    padding: 40px;
  }
}

.activeImageWrapper {
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  position: relative;

  .galleryInModal & {
    cursor: default;
  }
}

.controlWrapper {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  &:first-child {
    left: 0;
  }

  &:last-child {
    right: 0;
  }
}

.control {
  align-items: center;
  background-color: var(--purple);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 39px;
  justify-content: center;
  width: 39px;
}

.chevronLeft,
.chevronRight {
  color: var(--white);
  height: 13px;
  width: 8px;
}

.imageWrapper {
  display: flex;
  justify-content: center;
  padding: 10px 40px;
  width: 100%;
}

.image {
  display: block;
  height: auto;
  width: 100%;
}

.thumbnails {
  // Single row; slidesPerView set in JS, thumbnail size capped by max-width
  --thumbnails-gap: 10px;

  margin: 0 auto 15px;

  // 2 * 150 + 1 gap
  max-width: calc(2 * 150px + var(--thumbnails-gap));

  @include media('xs') {
    // 3 * 120 + 2 gaps
    max-width: calc(3 * 120px + 2 * var(--thumbnails-gap));
  }

  @include media('md') {
    // 3 * 100 + 3 gaps
    max-width: calc(3 * 100px + 3 * var(--thumbnails-gap));
  }
}

.thumbnailWrapper {
  background-color: var(--gray-1);
  border: 1px solid var(--gray-4);
  box-sizing: border-box;
  cursor: pointer;
  padding: 6px;
}

.thumbnail {
  display: block;
  height: auto;
  object-fit: contain;
  opacity: 0.28;
  width: 100%;

  &.active {
    opacity: 1;
  }
}

.pagination {
  align-items: center;
  display: flex;
  justify-content: center;
}

.dot {
  background-color: transparent;
  border: 1px solid var(--purple-light);
  border-radius: 50%;
  cursor: pointer;
  height: 11px;
  margin: 0 6px;
  width: 11px;

  &.active {
    background-color: var(--purple-light);
  }
}

.directToMarketingMaterials {
  text-align: center;
}

.modalContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  max-width: 900px;
  width: 90%;
  margin: 0 auto;
}

.modal {
  position: relative;
  width: 100%;
  max-height: 80%;
}

.closeBtn {
  position: absolute;
  top: -40px;
  right: 0;
}

.timesIcon {
  color: var(--white);
  width: 25px;
  height: 25px;
}
</style>
